import React, { useEffect, useMemo } from 'react';
import { useParams, useNavigate, useLocation, useSearchParams, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiChevronLeft } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { CreateRoundWizardProvider, useCreateRoundWizard } from '../contexts/CreateRoundWizardContext';
import StepWhen from './createRound/StepWhen';
import StepDetails from './createRound/StepDetails';
import StepWho from './createRound/StepWho';
import { getSchedulingWeekRange } from '../utils/dateHelpers';

const STEPS = ['when', 'details', 'who'];
const STEP_LABELS = {
  when: 'When do you want to play?',
  details: 'Round details',
  who: 'Who are you playing with?',
};

function WizardShell() {
  const { mode = 'smart', step = 'when' } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { state, setMode, hydrate, setWhoPickerOpen, reset } = useCreateRoundWizard();

  // Hydrate from navigation state OR URL query params (e.g. notification deep
  // links) on first render. Query params: ?group=<id>, ?who=friends|solo|group.
  useEffect(() => {
    const payload = {};

    const navState = location.state;
    if (navState?.selectedGroup) {
      payload.who = 'group';
      payload.selectedGroupId = navState.selectedGroup.id;
      payload.selectedGroupMemberIds = (navState.selectedGroup.members || [])
        .map(m => m.userId)
        .filter(Boolean);
    } else if (navState?.preselectGroupId) {
      payload.who = 'group';
      payload.selectedGroupId = navState.preselectGroupId;
    }

    if (payload.who == null) {
      const groupParam = searchParams.get('group');
      const whoParam = searchParams.get('who');
      if (groupParam) {
        const groupIdNum = Number(groupParam);
        payload.who = 'group';
        payload.selectedGroupId = Number.isFinite(groupIdNum) ? groupIdNum : groupParam;
      } else if (whoParam === 'friends' || whoParam === 'solo' || whoParam === 'group') {
        payload.who = whoParam;
      }
    }

    // If we have a "who" pre-selection, open the picker so it shows
    // immediately when the user reaches the WHO step (rather than asking
    // them to re-pick what we already know).
    if (payload.who === 'friends' || payload.who === 'group') {
      payload.whoPickerOpen = true;
    }

    // Smart-mode requires a date range for the availability summary to
    // render on the WHO step. StepWhen seeds this when the user walks the
    // flow normally, but deep links can land on /details or /who without
    // ever visiting /when — seed a sensible default (this week) here so the
    // availability badges render either way. StepWhen will leave it alone
    // since its own guard only runs when dateRange is undefined.
    if (mode === 'smart' && !payload.dateRange) {
      payload.dateRange = getSchedulingWeekRange(0);
    }

    if (Object.keys(payload).length) hydrate(payload);
    // Clear navigation state so back/forward doesn't re-hydrate stale data
    window.history.replaceState({ ...window.history.state, usr: null }, '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (state.mode !== mode) setMode(mode);
  }, [mode, state.mode, setMode]);

  const stepIndex = STEPS.indexOf(step);
  const validStep = stepIndex !== -1;

  if (!validStep) {
    return <Navigate to={`/create-round/${mode}/when`} replace />;
  }

  const handleBack = () => {
    // On the Who step, if the picker is open the Back button should collapse
    // it back to the three cards before leaving the step. This matches the
    // wizard's mental model: the picker is a sub-screen of step 3, not a
    // separate URL.
    if (step === 'who' && state.whoPickerOpen) {
      setWhoPickerOpen(false);
      return;
    }
    if (stepIndex === 0) {
      navigate('/dashboard');
    } else {
      navigate(`/create-round/${mode}/${STEPS[stepIndex - 1]}`);
    }
  };

  const switchModeTarget = mode === 'smart' ? 'quick' : 'smart';

  return (
    <div className="min-h-screen bg-app">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header: back + mode switch + progress */}
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={handleBack}
            className="flex items-center text-sm text-text-muted hover:text-text-secondary transition-colors min-h-[44px]"
          >
            <SafeIcon icon={FiChevronLeft} className="w-4 h-4 mr-1" />
            Back
          </button>
          <button
            onClick={() => {
              // Reset wizard state to the target mode's defaults — switching
              // mode mid-flow shouldn't drag along a smart-mode date range
              // into Quick (or vice versa) since the two modes use different
              // shape fields (smart: dateRange, quick: date+time). Always
              // land on the WHEN step regardless of where we were.
              reset(switchModeTarget);
              navigate(`/create-round/${switchModeTarget}/when`);
            }}
            className="text-xs text-text-muted hover:text-text-secondary transition-colors hover:underline underline-offset-2 min-h-[44px]"
          >
            Switch to {switchModeTarget === 'smart' ? 'Smart Schedule' : 'Quick Schedule'}
          </button>
        </div>

        {/* Progress indicator */}
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-3">
            {STEPS.map((s, i) => (
              <div
                key={s}
                className={`h-1 flex-1 rounded-full transition-colors ${
                  i <= stepIndex ? 'bg-accent' : 'bg-border-subtle'
                }`}
              />
            ))}
          </div>
          <div className="flex items-center justify-between gap-3">
            <h1 className="text-2xl font-bold text-text-primary">{STEP_LABELS[step]}</h1>
            <span className="text-xs text-text-muted font-medium whitespace-nowrap flex-shrink-0">
              Step {stepIndex + 1} of {STEPS.length}
            </span>
          </div>
        </div>

        <motion.div
          key={step}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.25 }}
        >
          {step === 'when' && <StepWhen />}
          {step === 'details' && <StepDetails />}
          {step === 'who' && <StepWho />}
        </motion.div>
      </div>
    </div>
  );
}

function CreateRoundWizard() {
  return (
    <CreateRoundWizardProvider>
      <WizardShell />
    </CreateRoundWizardProvider>
  );
}

export default CreateRoundWizard;
